import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

// Delay before showing the in-flight spinner, so fast responses don't flash it.
const LOADING_INDICATOR_DELAY = 300;

// Safety net: if no cart event ever comes back (dropped request, offline), release the
// button anyway instead of leaving the shopper with a permanently dead bar.
const LOADING_TIMEOUT = 10000;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isAddingToCart = false;

  /** @type {number | undefined} */
  #loadingIndicatorTimeout;

  /** @type {number | undefined} */
  #loadingFailsafeTimeout;

  /** @type {MutationObserver | null} */
  #targetButtonObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupSwatchListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetButtonObserver?.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#loadingIndicatorTimeout);
    clearTimeout(this.#loadingFailsafeTimeout);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // Cache the target up front: the early returns below only affect when the bar shows,
    // but without this the button would silently do nothing when they're hit.
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility - triggers early on mobile (first scroll)
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const buyButtonsMargin = isMobile ? '0px 0px 80% 0px' : '0px';

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    }, {
      rootMargin: buyButtonsMargin,
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // The main button is disabled while its request is in flight, so a repeat tap here
    // would be a silent no-op. Bail out early and keep the spinner up instead.
    if (this.#isAddingToCart) return;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;

    this.#setLoading(true);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    // Respect the global add_to_cart_animation setting
    const addToCartContainer = this.#targetAddToCartButton.closest('add-to-cart-component');
    const animationEnabled = addToCartContainer?.dataset.addToCartAnimation === 'true';
    if (!animationEnabled) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    // Re-attach swatch listeners after morph replaces DOM
    this.#setupSwatchListeners();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    this.#setLoading(false);
  };

  /**
   * Locks the sticky button while the add to cart request is in flight, so the shopper
   * gets immediate feedback instead of tapping again into a silent request.
   *
   * @param {boolean} loading - Whether a request is in flight.
   */
  #setLoading(loading) {
    const { addToCartButton } = this.refs;

    this.#isAddingToCart = loading;
    clearTimeout(this.#loadingIndicatorTimeout);
    clearTimeout(this.#loadingFailsafeTimeout);

    addToCartButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    // Once released, fall back to whatever the current variant allows.
    addToCartButton.disabled = loading ? true : this.dataset.variantAvailable !== 'true';

    if (loading) {
      this.#observeTargetButton();
      this.#loadingIndicatorTimeout = setTimeout(() => {
        addToCartButton.dataset.loading = 'true';
      }, LOADING_INDICATOR_DELAY);
      this.#loadingFailsafeTimeout = setTimeout(() => this.#setLoading(false), LOADING_TIMEOUT);
    } else {
      this.#targetButtonObserver?.disconnect();
      delete addToCartButton.dataset.loading;
    }
  }

  /**
   * Mirrors the main button's lock. The product form re-enables it in its `finally`, so
   * watching that attribute releases the sticky bar on success, server errors and
   * network failures alike, without depending on an event being dispatched.
   */
  #observeTargetButton() {
    this.#targetButtonObserver?.disconnect();

    const target = this.#targetAddToCartButton;
    if (!target) return;

    this.#targetButtonObserver = new MutationObserver(() => {
      if (!target.disabled) this.#setLoading(false);
    });
    this.#targetButtonObserver.observe(target, { attributes: true, attributeFilter: ['disabled'] });
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Sets up click listeners on sticky swatch buttons to trigger variant changes
   */
  #setupSwatchListeners() {
    const { signal } = this.#abortController;
    this.querySelectorAll('.sticky-atc-swatch').forEach((btn) => {
      btn.addEventListener('click', () => {
        const optionValue = btn.dataset.optionValue;
        const optionPosition = btn.dataset.optionPosition;
        if (!optionValue || !optionPosition) return;

        // Update active swatch state and label immediately
        const wrapper = btn.closest('.sticky-atc-swatches-wrapper');
        if (wrapper) {
          wrapper.querySelectorAll('.sticky-atc-swatch').forEach((s) => s.classList.remove('sticky-atc-swatch--active'));
          btn.classList.add('sticky-atc-swatch--active');
          const labelValue = wrapper.querySelector('.sticky-atc-swatches-label__value');
          if (labelValue) labelValue.textContent = optionValue;
        }

        // Find the matching radio input in the main variant picker and click it
        const productId = this.dataset.productId;
        const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
        if (!variantPicker) return;

        const matchingInput = variantPicker.querySelector(
          `input[type="radio"][data-input-id^="${optionPosition}-"][value="${CSS.escape(optionValue)}"]`
        );
        if (matchingInput && !matchingInput.checked) {
          matchingInput.click();
        }
      }, { signal });
    });
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { quantityDisplay, quantityNumber } = this.refs;

    // Read availability from the dataset rather than the button, which is also disabled
    // while an add to cart request is in flight.
    const available = this.dataset.variantAvailable === 'true';

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
